/**
 * Image Generator Modes Types & Intent-Based Built-in Definitions
 *
 * A "mode" configures all panels at once, unlike presets which configure a single panel.
 * Built-in modes are defined as intents that resolve dynamically from available entities,
 * eliminating hardcoded model names and provider params.
 */

import type {
  EnhancementKey,
  ProviderParams,
  ImageInstanceConfig,
  ProviderParamDefinition,
} from './image-generator.types';
import {
  NUM_INSTANCES,
  EMPTY_ENHANCEMENTS,
  EMPTY_PROVIDER_PARAMS,
  areEnhancementsEqual,
  areProviderParamsEqual,
  getActiveModelSettings,
} from './image-generator.types';
import type { ModelDefinitionListItem } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

/** Configuration for a single panel within a mode */
export interface ModeInstanceConfig {
  modelName: string;
  provider: string;
  size: string;
  quality: string;
  style: string;
  enhancements: Record<EnhancementKey, string>;
  providerParams: ProviderParams;
}

/** A mode configures all panels at once */
export interface ImageMode {
  id: string;
  name: string;
  description: string;
  builtIn: boolean;
  panelConfigs: ModeInstanceConfig[];
  tags: string[];
  createdAt: string;
}

// ============================================================================
// localStorage key
// ============================================================================

export function getModesStorageKey(userId: string): string {
  return `fias-image-modes-${userId}`;
}

// ============================================================================
// Panel Helpers
// ============================================================================

function panel(
  modelName: string,
  provider: string,
  overrides: Partial<ModeInstanceConfig> = {},
): ModeInstanceConfig {
  return {
    modelName,
    provider,
    size: '1:1',
    quality: 'standard',
    style: 'vivid',
    enhancements: { ...EMPTY_ENHANCEMENTS },
    providerParams: { ...EMPTY_PROVIDER_PARAMS },
    ...overrides,
  };
}

// ============================================================================
// Intent System — entity-driven model selection
// ============================================================================

/** How to pick a model for a panel */
interface PanelIntent {
  /** Model selection strategy */
  pick:
    | { by: 'fastest'; provider?: string }
    | { by: 'best'; provider?: string }
    | { by: 'middle'; provider?: string }
    | { by: 'cheapest'; provider?: string }
    | { by: 'capability'; match: string }
    | { by: 'any_from'; provider: string }
    | { by: 'most_params' };
  /** Size override (default: '1:1') */
  size?: string;
  /** Set quality-category param to max value */
  qualityLevel?: 'max';
  /** Set style-category param to a specific value */
  styleValue?: string;
  /** Direct providerParam overrides for non-categorized params */
  paramOverrides?: Record<string, unknown>;
  /** Use widest available size instead of a fixed size */
  widestSize?: boolean;
  /** Use non-default style values from entity data */
  nonDefaultStyle?: boolean;
  /** Use non-default param values for fine control */
  nonDefaultParams?: boolean;
}

/** Built-in mode definition with intent-based panel resolution */
interface BuiltInModeDefinition {
  id: string;
  name: string;
  description: string;
  panelIntents: PanelIntent[];
  /** Minimum panels needed to include this mode (default: 2) */
  minPanels?: number;
}

// ============================================================================
// Entity Selection Helpers
// ============================================================================

function getDuration(model: ModelDefinitionListItem): number {
  return (model.capabilities?.estimatedDurationMs as number) || 15000;
}

function getProviderParams(model: ModelDefinitionListItem): ProviderParamDefinition[] {
  const params = model.capabilities?.providerParams;
  return Array.isArray(params) ? (params as ProviderParamDefinition[]) : [];
}

/** Get unique providers from models, preserving insertion order */
function getProviders(models: ModelDefinitionListItem[]): string[] {
  return [...new Set(models.map((m) => m.provider))];
}

/** Sort models by estimated duration (ascending = fastest first) */
function sortByDuration(models: ModelDefinitionListItem[]): ModelDefinitionListItem[] {
  return [...models].sort((a, b) => getDuration(a) - getDuration(b));
}

/**
 * Whether a model can generate from a text prompt alone — i.e. it does NOT
 * require a reference image. Built-in modes are prompt-based batch generation,
 * so a reference-requiring (image-to-image-only) capability such as
 * `recraft-image-to-image` must never be auto-selected into a mode panel: it
 * would dead-end on "requires a reference image", and because it only supports
 * size `'source'` the panel's size gets rewritten, making the applied mode
 * read back as "Custom".
 *
 * A model is prompt-generatable when its `modes` include `text-to-image` or
 * `vector`. Models that ALSO do image-to-image (e.g. flux-2-pro, gpt-image-2)
 * still qualify via their `text-to-image` mode and stay eligible. When `modes`
 * is absent (legacy rows synced before the field landed) we assume generatable
 * so we never over-filter. Such image-to-image-only models remain fully
 * available in the per-panel model dropdown for users who deliberately want
 * image-to-image — this only governs automatic mode selection.
 */
function canGenerateFromPrompt(model: ModelDefinitionListItem): boolean {
  const modes = (model.capabilities?.modes as string[] | undefined) ?? [];
  if (modes.length > 0) {
    return modes.includes('text-to-image') || modes.includes('vector');
  }
  // Fallback for rows synced before `modes` landed (the data ships at deploy
  // via sync-entities, slightly behind a frontend release): a model whose only
  // supported size is 'source' produces output matching the input image's
  // dimensions, so it definitionally needs a reference image and can't
  // generate from a prompt alone.
  const sizes = (model.capabilities?.supportedSizes as string[] | undefined) ?? [];
  if (sizes.length === 1 && sizes[0] === 'source') return false;
  return true; // unknown → assume prompt-generatable
}

/** Pick model by strategy within a set */
function pickByStrategy(
  models: ModelDefinitionListItem[],
  strategy: PanelIntent['pick'],
): ModelDefinitionListItem | undefined {
  if (models.length === 0) return undefined;
  const sorted = sortByDuration(models);

  switch (strategy.by) {
    case 'fastest':
      return sorted[0];
    case 'cheapest':
      return sorted[0]; // fastest ≈ cheapest as a proxy
    case 'best':
      return sorted[sorted.length - 1];
    case 'middle':
      return sorted[Math.floor(sorted.length / 2)];
    case 'any_from': {
      const providerModels = sorted.filter((m) => m.provider === strategy.provider);
      return providerModels[Math.floor(providerModels.length / 2)] || undefined;
    }
    case 'capability': {
      const match = strategy.match.toLowerCase();
      const matching = models.filter((m) => {
        const name = m.modelName.toLowerCase();
        const strengths = ((m.capabilities?.strengths as string) || '').toLowerCase();
        return name.includes(match) || strengths.includes(match);
      });
      return matching.length > 0 ? matching[0] : undefined;
    }
    case 'most_params': {
      return [...models].sort(
        (a, b) => getProviderParams(b).length - getProviderParams(a).length,
      )[0];
    }
    default:
      return models[0];
  }
}

/** Resolve a panel intent to a ModeInstanceConfig */
function resolveIntent(
  intent: PanelIntent,
  models: ModelDefinitionListItem[],
): ModeInstanceConfig | null {
  // Filter by provider if specified
  const strategy = intent.pick;
  let candidateModels = models;
  if ('provider' in strategy && strategy.provider) {
    candidateModels = models.filter((m) => m.provider === strategy.provider);
  }

  const model = pickByStrategy(candidateModels, strategy);
  if (!model) return null;

  const paramDefs = getProviderParams(model);
  const providerParams: ProviderParams = {};
  let size = intent.size || '1:1';

  // Apply quality level
  if (intent.qualityLevel === 'max') {
    const qualityParam = paramDefs.find((d) => d.category === 'quality');
    if (qualityParam?.options && qualityParam.options.length > 0) {
      // Pick the last option (highest quality)
      providerParams[qualityParam.key] =
        qualityParam.options[qualityParam.options.length - 1].value;
    }
  }

  // Apply specific style value
  if (intent.styleValue) {
    const styleParam = paramDefs.find((d) => d.category === 'style');
    if (styleParam?.options) {
      const match = styleParam.options.find((o) => o.value === intent.styleValue);
      if (match) providerParams[styleParam.key] = match.value;
    }
  }

  // Apply non-default style
  if (intent.nonDefaultStyle) {
    const styleParam = paramDefs.find((d) => d.category === 'style');
    if (styleParam?.options && styleParam.options.length > 1) {
      const nonDefault = styleParam.options.find(
        (o) => o.value !== String(styleParam.defaultValue),
      );
      if (nonDefault) providerParams[styleParam.key] = nonDefault.value;
    }
  }

  // Apply non-default params for fine control
  if (intent.nonDefaultParams) {
    for (const def of paramDefs) {
      if (def.category) continue; // Skip quality/style
      if (def.controlType === 'slider' && typeof def.defaultValue === 'number') {
        providerParams[def.key] = def.defaultValue;
      } else if (def.controlType === 'checkbox') {
        providerParams[def.key] = true;
      } else if (def.controlType === 'textarea') {
        providerParams[def.key] = '';
      }
    }
  }

  // Apply widest size from entity capabilities
  if (intent.widestSize) {
    const caps = model.capabilities;
    const sizes = (caps?.supportedSizes as string[]) || [];
    const widest = findWidestSize(sizes);
    if (widest) size = widest;
  }

  // Apply direct param overrides
  if (intent.paramOverrides) {
    for (const [key, value] of Object.entries(intent.paramOverrides)) {
      providerParams[key] = value;
    }
  }

  return panel(model.modelName, model.provider, {
    size,
    providerParams:
      Object.keys(providerParams).length > 0 ? providerParams : { ...EMPTY_PROVIDER_PARAMS },
  });
}

/** Find the widest aspect ratio size from supported sizes */
function findWidestSize(sizes: string[]): string | null {
  let widest: string | null = null;
  let maxRatio = 0;
  for (const s of sizes) {
    const ratioMatch = s.match(/^(\d+):(\d+)$/);
    const pxMatch = s.match(/^(\d+)x(\d+)$/);
    let ratio = 1;
    if (ratioMatch) ratio = parseInt(ratioMatch[1]) / parseInt(ratioMatch[2]);
    else if (pxMatch) ratio = parseInt(pxMatch[1]) / parseInt(pxMatch[2]);
    if (ratio > maxRatio) {
      maxRatio = ratio;
      widest = s;
    }
  }
  return widest;
}

// ============================================================================
// Built-in Mode Intent Definitions
// ============================================================================

/** Generates per-provider intents: one panel per provider using the given strategy */
function perProviderIntents(
  models: ModelDefinitionListItem[],
  strategy: PanelIntent['pick']['by'],
  overrides: Partial<Omit<PanelIntent, 'pick'>> = {},
): PanelIntent[] {
  const providers = getProviders(models);
  return providers.slice(0, NUM_INSTANCES).map((provider) => ({
    pick: { by: strategy, provider } as PanelIntent['pick'],
    ...overrides,
  }));
}

const BUILT_IN_MODE_DEFINITIONS: BuiltInModeDefinition[] = [
  {
    id: 'mode_builtin_quick_draft',
    name: 'Quick Draft',
    description: 'Speed priority — fast turbo and lightweight models',
    panelIntents: [], // Filled dynamically with perProviderIntents
  },
  {
    id: 'mode_builtin_balanced',
    name: 'Balanced',
    description: 'Good quality with reasonable speed across providers',
    panelIntents: [],
  },
  {
    id: 'mode_builtin_maximum_quality',
    name: 'Maximum Quality',
    description: 'Best output from top-tier models',
    panelIntents: [],
  },
  {
    id: 'mode_builtin_provider_shootout',
    name: 'Provider Shootout',
    description: 'Compare output across different AI providers',
    panelIntents: [],
  },
  {
    id: 'mode_builtin_design_vector',
    name: 'Design & Vector',
    description: 'Logos, illustrations, and vector artwork',
    panelIntents: [
      { pick: { by: 'capability', match: 'vector' } },
      { pick: { by: 'capability', match: 'illustration' } },
      { pick: { by: 'capability', match: 'icon' } },
      { pick: { by: 'capability', match: 'vector' } },
      { pick: { by: 'capability', match: 'design' } },
    ],
    minPanels: 2,
  },
  {
    id: 'mode_builtin_photorealistic',
    name: 'Photorealistic',
    description: 'Photo-like output optimized for realism',
    panelIntents: [],
  },
  {
    id: 'mode_builtin_budget',
    name: 'Budget',
    description: 'Cheapest cost per image across providers',
    panelIntents: [],
  },
  {
    id: 'mode_builtin_creative_styles',
    name: 'Creative Styles',
    description: 'Artistic variety with cinematic, illustration, and vivid styles',
    panelIntents: [],
  },
  {
    id: 'mode_builtin_text_typography',
    name: 'Text & Typography',
    description: 'Best text rendering across providers',
    panelIntents: [],
  },
  {
    id: 'mode_builtin_high_resolution',
    name: 'High Resolution',
    description: 'Print-quality, large format output',
    panelIntents: [],
  },
  {
    id: 'mode_builtin_cinematic_widescreen',
    name: 'Cinematic Widescreen',
    description: 'Film and video frame compositions',
    panelIntents: [],
  },
  {
    id: 'mode_builtin_fine_control',
    name: 'Fine Control',
    description: 'Tunable parameters — steps, guidance, CFG, raw mode',
    panelIntents: [
      { pick: { by: 'most_params' }, nonDefaultParams: true },
      { pick: { by: 'most_params' }, nonDefaultParams: true },
      { pick: { by: 'most_params' }, nonDefaultParams: true },
      { pick: { by: 'most_params' }, nonDefaultParams: true },
      { pick: { by: 'most_params' }, nonDefaultParams: true },
    ],
    minPanels: 2,
  },
];

/** Resolve intents for modes that use per-provider strategies */
function resolveIntentsForMode(
  def: BuiltInModeDefinition,
  models: ModelDefinitionListItem[],
): PanelIntent[] {
  // Modes with explicit intents use those directly
  if (def.panelIntents.length > 0) return def.panelIntents;

  // Generate per-provider intents based on mode type
  switch (def.id) {
    case 'mode_builtin_quick_draft':
      return perProviderIntents(models, 'fastest');
    case 'mode_builtin_balanced':
      return perProviderIntents(models, 'middle');
    case 'mode_builtin_maximum_quality':
      return perProviderIntents(models, 'best', { qualityLevel: 'max' });
    case 'mode_builtin_provider_shootout':
      return perProviderIntents(models, 'middle');
    case 'mode_builtin_photorealistic':
      return perProviderIntents(models, 'best', { styleValue: 'photographic' });
    case 'mode_builtin_budget':
      return perProviderIntents(models, 'cheapest');
    case 'mode_builtin_creative_styles':
      return perProviderIntents(models, 'best', { nonDefaultStyle: true });
    case 'mode_builtin_text_typography':
      return perProviderIntents(models, 'best', { qualityLevel: 'max' });
    case 'mode_builtin_high_resolution':
      return perProviderIntents(models, 'best', { widestSize: true });
    case 'mode_builtin_cinematic_widescreen':
      return perProviderIntents(models, 'best', { size: '16:9', widestSize: true });
    default:
      return perProviderIntents(models, 'middle');
  }
}

// ============================================================================
// Static Fallback Modes (used when no entity data is available)
// ============================================================================

/**
 * Fallback built-in modes with hardcoded model names.
 * Only used on initial load before entity data arrives.
 */
export const BUILT_IN_MODES: ImageMode[] = [
  {
    id: 'mode_builtin_quick_draft',
    name: 'Quick Draft',
    description: 'Speed priority — fast turbo and lightweight models',
    builtIn: true,
    panelConfigs: [
      panel('gpt-image-1', 'openai'),
      panel('gpt-image-1', 'openai'),
      panel('gpt-image-1', 'openai'),
      panel('gpt-image-1', 'openai'),
      panel('gpt-image-1', 'openai'),
    ],
    tags: [],
    createdAt: '2026-01-01T00:00:00.000Z',
  },
];

// ============================================================================
// Public API — Dynamic Resolution
// ============================================================================

/**
 * Returns the full set of built-in modes, resolved from available model entities.
 * Uses entity capabilities (estimatedDurationMs, providerParams, strengths, supportedSizes)
 * to dynamically pick models and configure panels instead of hardcoding.
 */
export function resolveBuiltInModes(models: ModelDefinitionListItem[]): ImageMode[] {
  if (models.length === 0) return BUILT_IN_MODES;

  // Built-in modes are prompt-based batch generation across panels, so they
  // must only ever auto-select models that can run from a prompt. Excluding
  // reference-requiring (image-to-image-only) capabilities here is what keeps
  // "Quick Draft" from picking e.g. recraft-image-to-image and dead-ending on
  // "requires a reference image". These models stay in the per-panel dropdown
  // for deliberate image-to-image use — see canGenerateFromPrompt.
  const generationModels = models.filter(canGenerateFromPrompt);
  if (generationModels.length === 0) return BUILT_IN_MODES;

  const resolved: ImageMode[] = [];

  for (const def of BUILT_IN_MODE_DEFINITIONS) {
    const intents = resolveIntentsForMode(def, generationModels);
    const minPanels = def.minPanels ?? 2;

    // Resolve each intent to a panel config
    const panels: ModeInstanceConfig[] = [];
    for (const intent of intents) {
      const config = resolveIntent(intent, generationModels);
      if (config) panels.push(config);
      if (panels.length >= NUM_INSTANCES) break;
    }

    // Skip mode if not enough panels resolved
    if (panels.length < minPanels) continue;

    // Pad with duplicates if fewer than NUM_INSTANCES
    while (panels.length < NUM_INSTANCES) {
      panels.push({ ...panels[panels.length - 1] });
    }

    resolved.push({
      id: def.id,
      name: def.name,
      description: def.description,
      builtIn: true,
      panelConfigs: panels,
      tags: [],
      createdAt: '2026-01-01T00:00:00.000Z',
    });
  }

  return resolved;
}

// ============================================================================
// Detection
// ============================================================================

/** Fields compared when detecting whether the current config matches a mode panel */
/**
 * Compares a mode's panel recipe against a live panel.
 *
 * `size` / `quality` / `style` / `providerParams` come from the panel's ACTIVE
 * model entry only — settings it remembers for other models are irrelevant here,
 * or switching a panel's model and back would break mode detection forever.
 */
function panelMatchesConfig(
  panelConfig: ModeInstanceConfig,
  instanceConfig: ImageInstanceConfig,
): boolean {
  if (panelConfig.modelName !== instanceConfig.modelName) return false;
  if (panelConfig.provider !== instanceConfig.provider) return false;

  const settings = getActiveModelSettings(instanceConfig);
  if (panelConfig.size !== settings.size) return false;
  if (panelConfig.quality !== settings.quality) return false;
  if (panelConfig.style !== settings.style) return false;

  if (!areEnhancementsEqual(panelConfig.enhancements || {}, instanceConfig.enhancements || {})) {
    return false;
  }
  return areProviderParamsEqual(panelConfig.providerParams || {}, settings.providerParams);
}

/**
 * Compares current panel configuration against all modes.
 * Returns the matching mode, or null if no match (i.e., "Custom").
 */
export function detectActiveMode(
  instanceConfigs: ImageInstanceConfig[],
  allModes: ImageMode[],
): ImageMode | null {
  if (instanceConfigs.length < NUM_INSTANCES) return null;

  for (const mode of allModes) {
    if (mode.panelConfigs.length !== NUM_INSTANCES) continue;
    const allMatch = mode.panelConfigs.every((panelConfig, i) =>
      panelMatchesConfig(panelConfig, instanceConfigs[i]),
    );
    if (allMatch) return mode;
  }
  return null;
}
